import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Truck, ExternalLink, Lock, Unlock, CheckCircle, Star, DollarSign, Ruler, Users, Zap, Home, Phone, Calendar, Crown, Book, BookOpen, Gift, ArrowRight, Sparkles, Heart, LayoutDashboard, History, Wrench, Building2, Menu, X, ChevronRight, Bell, Compass, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { formatUsPhoneInput } from '../utils/phone';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';
import ToolSalesGate from '../components/ToolSalesGate';
import MyToolsPanel from '../components/MyToolsPanel';
import RetakeAndHistoryPanel from '../components/RetakeAndHistoryPanel';
import MyApplicationPanel from '../components/MyApplicationPanel';
import VipBenefitsPanel from '../components/VipBenefitsPanel';
import DealerCardDisplay from '../components/DealerCardDisplay';
import MatchesPrintActions from '../components/MatchesPrintActions';
import MyToolsSection from '../components/dashboard/MyToolsSection';
import VipBookingCard from '../components/dashboard/VipBookingCard';
import UnifiedDashboardNudge from '../components/dashboard/UnifiedDashboardNudge';
import DailyMatchesFeed from '../components/dashboard/DailyMatchesFeed';
import { useFavorites } from '../hooks/useFavorites';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MyMatchesPage - Personal dashboard for quiz users
 * Shows their RV matches after completing the quiz and profile
 */
const MyMatchesPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  // Customer matches dashboard is wrapped in a cinematic dark background (bg-[#05070d]).
  // We force isDark=true here so every nested section renders with proper dark-mode contrast,
  // regardless of the user's global theme preference. Avoids light/dark mismatch inside the page.
  useTheme();
  const isDark = true;
  const { isFavorite, toggleFavorite } = useFavorites();
  
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [lead, setLead] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showUnlockForm, setShowUnlockForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipcode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSalesInterstitial, setShowSalesInterstitial] = useState(false);
  const [salesGateDismissed, setSalesGateDismissed] = useState(false);
  const [matchesPending, setMatchesPending] = useState(false);  // Polling flag for async match generation
  // Land buyers directly on their matches — that's what they came for.
  const [activeView, setActiveView] = useState('matches');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // First-visit celebratory reveal — fires only when the user arrives here
  // straight from the application submit flow (?welcome=1). Adds a subtle
  // glow-in around the top match + ebook rewards for 2s.
  const [showWelcomeReveal, setShowWelcomeReveal] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') === '1') {
      setShowWelcomeReveal(true);
      const t = setTimeout(() => setShowWelcomeReveal(false), 4200);
      // Clean the URL so refresh doesn't retrigger
      try { window.history.replaceState({}, '', window.location.pathname); } catch { /* ignore */ }
      return () => clearTimeout(t);
    }
  }, []);

  const handleToggleFavorite = (rvData) => {
    const added = toggleFavorite(rvData);
    if (isFavorite(rvData.id)) {
      toast.success('Removed from favorites');
    } else {
      toast.success('Added to favorites!');
    }
  };

  // Track page view when matches are viewed
  const trackPageView = async () => {
    try {
      await axios.post(`${API}/track/page-view`, { session_id: sessionId });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  // Track RV link clicks
  const trackClick = async (rv, linkType = 'search') => {
    try {
      await axios.post(`${API}/track/click`, {
        session_id: sessionId,
        rv_year: rv.year,
        rv_brand: rv.brand,
        rv_model: rv.model,
        link_type: linkType,
        source: 'web'
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  useEffect(() => {
    fetchSessionAndMatches();
  }, [sessionId]);

  // Poll every 2.5s while matches are still being generated in the background
  useEffect(() => {
    if (!matchesPending) return;
    const interval = setInterval(async () => {
      try {
        const leadResponse = await axios.get(`${API}/leads/by-session/${sessionId}`);
        if (leadResponse.data?.matched_rvs?.length > 0) {
          setLead(leadResponse.data);
          setMatchesPending(false);
        } else {
          // Also check session status so we can stop polling if it failed
          const sessionResponse = await axios.get(`${API}/quiz/session/${sessionId}`);
          if (sessionResponse.data?.status === 'matches_failed') {
            setMatchesPending(false);
            toast.error('We had trouble generating matches. Please try again.');
          }
        }
      } catch (e) { /* keep polling on transient errors */ }
    }, 2500);
    // Hard stop after 90s to avoid infinite polling
    const timeout = setTimeout(() => setMatchesPending(false), 90000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [matchesPending, sessionId]);

  const fetchSessionAndMatches = async () => {
    try {
      setIsLoading(true);
      
      // First, get the session info
      const sessionResponse = await axios.get(`${API}/quiz/session/${sessionId}`);
      setSession(sessionResponse.data);
      
      // Pre-fill form with existing data
      if (sessionResponse.data.email) {
        setFormData(prev => ({ ...prev, email: sessionResponse.data.email }));
      }
      if (sessionResponse.data.zipcode) {
        setFormData(prev => ({ ...prev, zipcode: sessionResponse.data.zipcode }));
      }
      
      // Check if lead exists and has profile completed
      try {
        const leadResponse = await axios.get(`${API}/leads/by-session/${sessionId}`);
        setLead(leadResponse.data);

        const hasMatches = leadResponse.data.matched_rvs?.length > 0;
        const profileDone = leadResponse.data.profile_completed || leadResponse.data.source === 'application_form';

        if (profileDone && !hasMatches) {
          // Application submitted but AI matches still generating in background — start polling
          setMatchesPending(true);
          setIsUnlocked(true);  // Already past the gate since profile is complete
          return;
        }

        if (leadResponse.data.profile_completed && hasMatches) {
          // Check if user already has tool access or has already seen/dismissed gate
          const gateKey = `rv_gate_dismissed_${sessionId}`;
          const alreadyDismissed = localStorage.getItem(gateKey);
          
          if (!alreadyDismissed) {
            // Check if they already purchased tools or are VIP
            try {
              const accessRes = await axios.get(`${API}/tools/access/${sessionId}`);
              if (accessRes.data.has_access) {
                setIsUnlocked(true);
                trackPageView();
              } else {
                // Show sales gate first
                setShowSalesInterstitial(true);
                setIsUnlocked(true);
              }
            } catch {
              // If access check fails, show gate anyway
              setShowSalesInterstitial(true);
              setIsUnlocked(true);
            }
          } else {
            setSalesGateDismissed(true);
            setIsUnlocked(true);
            trackPageView();
          }
        } else {
          setShowUnlockForm(true);
        }
      } catch (leadError) {
        // Lead might not exist yet - show unlock form
        setShowUnlockForm(true);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error('Session not found');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Complete profile to unlock matches
      const digitsOnlyPhone = formData.phone ? formData.phone.replace(/\D/g, '').slice(-10) : '';

      await axios.post(`${API}/leads/complete-profile`, {
        session_id: sessionId,
        ...formData,
        phone: digitsOnlyPhone
      });
      
      toast.success('Your matches are ready!');
      
      // Show sales gate interstitial before revealing matches
      setShowUnlockForm(false);
      setShowSalesInterstitial(true);
      setIsUnlocked(true);
    } catch (error) {
      console.error('Error unlocking matches:', error);
      toast.error('Failed to unlock matches. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#05070d]" data-testid="loading-state">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#F2A900] mx-auto mb-4" size={48} />
          <p className="text-gray-300">Loading your matches...</p>
        </div>
      </div>
    );
  }

  // Sales interstitial removed — MyToolsPanel now handles the upsell inline,
  // and the user explicitly wants tools integrated with the match profile,
  // not blocking it. Going straight to matches.

  // Dashboard layout — sidebar + main content area with section switching
  const isVipMember = !!(lead?.is_vip || (lead?.unlocked_tools || []).length > 0);
  const firstName = lead?.first_name || lead?.name?.split(' ')[0] || 'there';

  const navItems = [
    { id: 'overview',    label: 'Dashboard',       Icon: LayoutDashboard },
    { id: 'matches',     label: 'My Matches',      Icon: Compass, badge: lead?.matched_rvs?.length || 0 },
    { id: 'daily',       label: 'Daily Matches',   Icon: Bell },
    { id: 'tools',       label: 'My Tools',        Icon: Wrench, badge: (lead?.unlocked_tools || []).length || 0 },
    { id: 'resources',   label: 'Free Resources',  Icon: BookOpen, badge: 2 },
    { id: 'vip',         label: 'VIP Membership',  Icon: Crown, dot: !isVipMember },
    { id: 'application', label: 'My Application',  Icon: History },
    { id: 'dealer',      label: 'My Advisor',      Icon: Building2 },
  ];

  // Dashboard frame is only used when unlocked. Loading + unlock form retain their original look.
  if (isUnlocked) {
    return (
      <div className="min-h-screen bg-[#05070d] text-white relative" data-testid="my-matches-page">
        {/* Celebratory welcome reveal — fires once, only on ?welcome=1 arrival */}
        {showWelcomeReveal && (
          <div
            className="fixed inset-0 z-[110] pointer-events-none flex items-start justify-center"
            data-testid="welcome-reveal-overlay"
          >
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] welcome-fade" />
            <div className="relative mt-24 sm:mt-32 px-6 py-6 sm:px-10 sm:py-8 rounded-3xl border border-cyan-400/40 bg-gradient-to-br from-[#0b1428] to-[#05070d] shadow-[0_30px_100px_-20px_rgba(56,189,248,0.55)] welcome-pop">
              <p className="text-[10px] tracking-[0.3em] font-bold text-cyan-400 text-center mb-2">RV MATCH A.I.</p>
              <h2 className="text-2xl sm:text-4xl font-black text-white text-center leading-tight">
                Your matches are <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-indigo-300">ready.</span>
              </h2>
              <p className="text-sm text-slate-400 text-center mt-2">Both playbooks + 3 matches unlocked.</p>
            </div>
            <style>{`
              @keyframes welcomeFadeAnim {
                0% { opacity: 0; }
                20% { opacity: 1; }
                80% { opacity: 1; }
                100% { opacity: 0; }
              }
              @keyframes welcomePopAnim {
                0% { opacity: 0; transform: translateY(-16px) scale(0.94); }
                20% { opacity: 1; transform: translateY(0) scale(1); }
                80% { opacity: 1; transform: translateY(0) scale(1); }
                100% { opacity: 0; transform: translateY(-8px) scale(0.98); }
              }
              .welcome-fade { animation: welcomeFadeAnim 4.2s ease forwards; }
              .welcome-pop  { animation: welcomePopAnim 4.2s cubic-bezier(.2,.9,.3,1) forwards; }
            `}</style>
          </div>
        )}
        {/* Background atmosphere — matches the homepage cinematic theme */}
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-12 blur-[140px]" />
          <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-indigo-500 opacity-12 blur-[140px]" />
        </div>
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-[#05070d]/85 border-b border-white/10">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="p-2 rounded-lg text-slate-300 hover:bg-white/10"
              data-testid="customer-mobile-nav-toggle"
              aria-label="Toggle navigation"
            >
              {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {navItems.find((n) => n.id === activeView)?.label || 'Dashboard'}
            </p>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-[#F2A900] text-black' : 'bg-[#F2A900] text-black'}`}>
              {(firstName?.[0] || 'M').toUpperCase()}
            </div>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar — desktop sticky, mobile drawer. No internal scroll on desktop. */}
          <aside
            className={`fixed top-0 left-0 h-screen z-40 w-72 transform transition-transform duration-200 lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:shrink-0 ${
              mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
            } ${isDark ? 'bg-gray-900 border-r border-gray-800' : 'bg-white border-r border-amber-200'}`}
            data-testid="customer-sidebar"
            data-drawer-open={mobileNavOpen ? 'true' : 'false'}
          >
            <div className="h-full flex flex-col p-4 sm:p-5 overflow-y-auto lg:overflow-visible" data-testid="customer-sidebar-drawer">
              {/* Brand mark — keeps the customer dashboard visually anchored to the platform */}
              <div className="px-1 pt-1 pb-3 mb-2">
                <BrandLogo size="sm" variant="wordmark" />
              </div>
              {/* Member identity card */}
              <div className={`rounded-2xl p-4 mb-5 ${isDark ? 'bg-gradient-to-br from-amber-900/40 to-orange-900/30 border border-amber-700/50' : 'bg-gradient-to-br from-[#004E86] to-[#003d6b] text-white'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F2A900] flex items-center justify-center font-bold text-black text-lg shrink-0">
                    {(firstName?.[0] || 'M').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[11px] uppercase tracking-wider font-semibold ${isDark ? 'text-amber-300' : 'text-amber-300'}`}>
                      {isVipMember ? 'VIP Member' : 'Member'}
                    </p>
                    <p className={`font-bold truncate ${isDark ? 'text-white' : 'text-white'}`}>
                      {firstName}
                    </p>
                    <p className={`text-xs truncate ${isDark ? 'text-amber-200/70' : 'text-blue-100'}`}>
                      {lead?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Nav items — no internal scroll on desktop; the whole sidebar is sticky and fits. */}
              <nav className="space-y-1 flex-1 min-h-0">
                {navItems.map(({ id, label, Icon, badge, dot }) => {
                  const active = activeView === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { setActiveView(id); setMobileNavOpen(false); window.scrollTo(0, 0); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-semibold ${
                        active
                          ? 'bg-[#F2A900] text-black shadow-lg shadow-[#F2A900]/20'
                          : isDark
                            ? 'text-gray-300 hover:bg-gray-800'
                            : 'text-gray-700 hover:bg-amber-100'
                      }`}
                      data-testid={`customer-nav-${id}`}
                    >
                      <Icon size={18} className="shrink-0" />
                      <span className="flex-1 text-left">{label}</span>
                      {badge ? (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          active ? 'bg-black/15 text-black' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-amber-200 text-amber-900'
                        }`}>{badge}</span>
                      ) : null}
                      {dot ? (
                        <span className="w-2 h-2 rounded-full bg-rose-500" />
                      ) : null}
                    </button>
                  );
                })}
              </nav>

              {/* Footer — quick return to dashboard overview */}
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-800' : 'border-amber-200'}`}>
                <button
                  type="button"
                  onClick={() => { setActiveView('overview'); setMobileNavOpen(false); window.scrollTo(0, 0); }}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-amber-100'}`}
                  data-testid="customer-back-home"
                >
                  <Home size={16} /> Back to dashboard
                </button>
              </div>
            </div>
          </aside>

          {/* Mobile backdrop */}
          {mobileNavOpen && (
            <div
              className="lg:hidden fixed inset-0 bg-black/40 z-30"
              onClick={() => setMobileNavOpen(false)}
              data-testid="customer-mobile-backdrop"
            />
          )}

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Hero header */}
            <div className={`relative overflow-hidden ${isDark ? 'bg-gradient-to-br from-amber-900/30 via-gray-900 to-gray-900' : 'bg-gradient-to-br from-[#004E86] via-[#003d6b] to-[#002d50]'}`}>
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F2A900]/15 blur-3xl pointer-events-none" />
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10 relative">
                <p className="text-[#F2A900] text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2">
                  {isVipMember ? '★ VIP Member Dashboard' : 'Member Dashboard'}
                </p>
                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Welcome back, {firstName}.
                </h1>
                <p className="text-blue-100 mt-1.5 sm:mt-2 text-xs sm:text-sm lg:text-base max-w-2xl">
                  {activeView === 'overview' && 'Here\'s your personal RV-buying command center.'}
                  {activeView === 'matches' && 'Your AI-curated RV recommendations, refreshed and ready.'}
                  {activeView === 'daily' && 'Fresh, hand-picked RVs delivered right here — every day, for life.'}
                  {activeView === 'tools' && 'Every RV Match tool in one place — grab what you need, when you need it.'}
                  {activeView === 'resources' && 'Two field-tested playbooks — yours free with your application.'}
                  {activeView === 'vip' && 'One-on-one advisor time — pick the plan that fits.'}
                  {activeView === 'application' && 'Edit your answers, retake the application, or revisit a past attempt.'}
                  {activeView === 'dealer' && 'Your dedicated advisor and booking options.'}
                </p>
              </div>
            </div>

            <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
              {renderSection({
                activeView,
                lead,
                sessionId,
                isDark,
                fetchSessionAndMatches,
                isLoading,
                trackClick,
                handleToggleFavorite,
                isFavorite,
                matchesPending,
                setActiveView,
              })}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ============================================================
  // Pre-unlock states: loading, unlock form, no-matches
  // ============================================================
  return (
    <div className="min-h-screen bg-[#05070d] text-white relative" data-testid="my-matches-page-pre-unlock">
      {/* Background atmosphere */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-15 blur-[140px]" />
        <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-indigo-500 opacity-15 blur-[140px]" />
      </div>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="py-12 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="page-title"
            >
              Unlock Your RV Matches
            </h1>
            <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
              Complete your profile to see your personalized RV recommendations
            </p>
          </div>
        </div>
        <div className="h-16 bg-[#05070d]">
          <svg viewBox="0 0 1440 120" className="w-full h-full" preserveAspectRatio="none">
            <path fill="#0b1020" d="M0,0 C480,120 960,120 1440,0 L1440,0 L0,0 Z" />
          </svg>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-16 mt-6">
        {/* Unlock Form */}
        {showUnlockForm && (
          <div className={`max-w-xl mx-auto ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl overflow-hidden`} data-testid="unlock-form">
            <div className="bg-gradient-to-r from-[#F2A900] to-[#D99700] p-6 text-center">
              <Lock className="mx-auto mb-3 text-black" size={40} />
              <h2 className="text-2xl font-bold text-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                UNLOCK YOUR 3 RV MATCHES
              </h2>
              <p className="text-black/80 mt-2">Tell us a bit about yourself to see your personalized results</p>
            </div>

            <form onSubmit={handleUnlock} className="p-8 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className={isDark ? 'text-gray-300' : ''}>First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                    data-testid="unlock-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className={isDark ? 'text-gray-300' : ''}>Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                    className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                    data-testid="unlock-last-name"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className={isDark ? 'text-gray-300' : ''}>Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  data-testid="unlock-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className={isDark ? 'text-gray-300' : ''}>Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatUsPhoneInput(e.target.value) })}
                  required
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  data-testid="unlock-phone"
                />
              </div>
              <div>
                <Label htmlFor="zipcode" className={isDark ? 'text-gray-300' : ''}>Zipcode</Label>
                <Input
                  id="zipcode"
                  type="text"
                  value={formData.zipcode}
                  onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
                  maxLength={5}
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  data-testid="unlock-zipcode"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#F2A900] hover:bg-[#D99700] text-black font-bold uppercase tracking-wide py-4 text-lg"
                data-testid="unlock-submit"
              >
                {isSubmitting ? (
                  <><Loader2 className="animate-spin mr-2" size={20} />Unlocking...</>
                ) : (
                  <><Unlock className="mr-2" size={20} />Unlock My Matches</>
                )}
              </Button>
              <p className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                By submitting, you agree to receive RV recommendations via email.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * renderSection — picks which section to show in the dashboard main area.
 * Pulled out as a function so MyMatchesPage stays readable.
 */
const renderSection = ({ activeView, lead, sessionId, isDark, fetchSessionAndMatches, isLoading, trackClick, handleToggleFavorite, isFavorite, matchesPending, setActiveView }) => {
  const matches = lead?.matched_rvs || [];
  const tools = lead?.unlocked_tools || [];
  const isVipMember = !!(lead?.is_vip || tools.length > 0);

  if (activeView === 'overview') {
    return <OverviewSection
      lead={lead}
      sessionId={sessionId}
      isDark={isDark}
      isVipMember={isVipMember}
      matches={matches}
      tools={tools}
      setActiveView={setActiveView}
      matchesPending={matchesPending}
    />;
  }

  if (activeView === 'matches') {
    if (matchesPending) {
      return (
        <div className={`${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white'} rounded-2xl shadow-sm p-12 text-center`} data-testid="matches-pending">
          <Loader2 className="mx-auto mb-4 animate-spin text-[#F2A900]" size={56} />
          <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Finding Your Perfect Matches…</h3>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Our AI is scanning inventory. This usually takes 10–20 seconds.</p>
        </div>
      );
    }
    if (matches.length === 0) {
      return (
        <div className={`${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white'} rounded-2xl shadow-sm p-12 text-center`} data-testid="no-matches">
          <Truck className={`mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} size={56} />
          <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Matches Being Prepared</h3>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Your personalized RV matches are being generated. Check your email shortly!</p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        {lead?.matching_criteria && <CriteriaSummary lead={lead} isDark={isDark} />}
        <MatchesPrintActions lead={lead} sessionId={sessionId} isDark={isDark} />
        <div className="grid gap-6">
          {matches.map((match, idx) => {
            const rv = match.rv || {};
            const rvId = `${rv.year}-${rv.brand}-${rv.model}-${idx}`;
            return (
              <RVMatchCard
                key={idx}
                match={match}
                index={idx}
                isDark={isDark}
                sessionId={sessionId}
                rvId={String(idx)}
                onTrackClick={trackClick}
                onToggleFavorite={handleToggleFavorite}
                isFavorited={isFavorite(rvId)}
                lead={lead}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (activeView === 'tools') {
    return (
      <div className="space-y-6">
        {/* New unified catalog: shows every consumer product (tools +
            bundle + VIP) with per-tile owned/buyable state. Replaces the
            legacy free-tool teaser + unlock CTA panel. */}
        <MyToolsSection email={lead?.email} sessionId={sessionId} />
      </div>
    );
  }

  if (activeView === 'vip') {
    return (
      <div className="space-y-6">
        {/* Unified VIP catalog — Buying Plan + Concierge purchased via
            Stripe. Replaces the legacy PayPal VIP flow. */}
        <MyToolsSection email={lead?.email} filter="vip" sessionId={sessionId} />
      </div>
    );
  }

  if (activeView === 'resources') {
    return <FreeResourcesSection sessionId={sessionId} isDark={isDark} />;
  }

  if (activeView === 'application') {
    return <MyApplicationPanel lead={lead} isDark={isDark} />;
  }

  if (activeView === 'daily') {
    return <DailyMatchesFeed email={lead?.email} />;
  }

  if (activeView === 'dealer') {
    return (
      <div className="space-y-6">
        <DealerCardDisplay dealerCard={lead?.dealer_card} isDark={isDark} />
        <BookCallSection sessionId={sessionId} lead={lead} isDark={isDark} />
      </div>
    );
  }

  return null;
};

/**
 * OverviewSection — dashboard "home" with stat tiles + quick previews.
 */
const OverviewSection = ({ lead, sessionId, isDark, isVipMember, matches, tools, setActiveView, matchesPending }) => {
  // Referral milestone — poll once on mount to see if this user has already
  // referred anyone. If yes, show a celebratory reward badge.
  const [referralStats, setReferralStats] = useState(null);
  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/referrals/stats/${sessionId}`)
      .then(res => setReferralStats(res.data))
      .catch(() => setReferralStats(null));
  }, [sessionId]);
  const hasFirstReferral = (referralStats?.referred_count || 0) >= 1;
  const referralCount = referralStats?.referred_count || 0;
  const stats = [
    {
      label: 'RV Matches',
      value: matchesPending ? '—' : matches.length,
      sub: matchesPending ? 'Generating…' : 'AI-curated',
      Icon: Compass,
      color: 'text-blue-500',
      bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
      onClick: () => setActiveView('matches'),
    },
    {
      label: 'Tools Unlocked',
      value: `${tools.length}/3`,
      sub: tools.length === 3 ? 'Full bundle' : tools.length > 0 ? 'Bundle access' : 'Bundle locked',
      Icon: Wrench,
      color: 'text-emerald-500',
      bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
      onClick: () => setActiveView('tools'),
    },
    {
      label: 'Membership',
      value: isVipMember ? 'VIP' : 'Free',
      sub: isVipMember ? 'Daily matches active' : 'Upgrade for daily matches',
      Icon: Crown,
      color: 'text-amber-500',
      bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50',
      onClick: () => setActiveView('vip'),
    },
  ];

  return (
    <div className="space-y-6" data-testid="overview-section">
      {/* Referral milestone banner — only when user has 1+ friends referred */}
      {hasFirstReferral && (        <div
          data-testid="referral-milestone-badge"
          className={`rounded-2xl p-4 sm:p-5 border ${
            isDark
              ? 'bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent border-amber-400/30'
              : 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300'
          } flex items-center gap-4`}
        >
          <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${
            isDark ? 'bg-amber-500/20 border border-amber-400/40' : 'bg-white shadow-sm'
          }`}>🏆</div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
              You've referred {referralCount} {referralCount === 1 ? 'friend' : 'friends'} — nice work!
            </p>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-amber-100/70' : 'text-amber-800/80'}`}>
              {referralStats?.converted_count > 0
                ? `${referralStats.converted_count} converted to VIP — ${referralStats.available ? `$${referralStats.available} cashable` : 'reward earned'}.`
                : `Earn $50 each time one becomes a VIP member.`}
            </p>
          </div>
          <button
            onClick={() => setActiveView('vip')}
            className={`shrink-0 px-3 sm:px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap ${
              isDark ? 'bg-[#F2A900] text-black hover:bg-amber-400' : 'bg-[#F2A900] text-black hover:bg-amber-500'
            } transition-colors`}
            data-testid="referral-milestone-cta"
          >
            View rewards →
          </button>
        </div>
      )}

      {/* SMS match alert opt-in — coming soon, but capture the signal now */}
      <SmsOptInCard sessionId={sessionId} lead={lead} isDark={isDark} />
      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={s.onClick}
            className={`text-left rounded-2xl p-5 transition-all hover:shadow-md ${isDark ? 'bg-gray-900 border border-gray-800 hover:border-gray-700' : 'bg-white border border-amber-200 hover:border-amber-400'}`}
            data-testid={`overview-stat-${i}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg}`}>
                <s.Icon className={s.color} size={20} />
              </div>
              <ChevronRight size={16} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
            </div>
            <p className={`text-2xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.value}</p>
            <p className={`text-sm font-semibold mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{s.label}</p>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{s.sub}</p>
          </button>
        ))}
      </div>

      {/* My Tools — every paid product tied to this buyer's email. Only
          renders if they've actually purchased something; silent otherwise. */}
      <MyToolsSection email={lead?.email} sessionId={sessionId} />

      {/* Nudge quiz-only buyers to explore the tools ladder. Silent for
          buyers who already own at least one product. */}
      <UnifiedDashboardNudge variant="tools" email={lead?.email} sessionId={sessionId} />

      {/* VIP booking card — silent unless the buyer has an open VIP case.
          Auto-opens the booking form when arriving from the purchase email
          via ?vip=book. */}
      <VipBookingCard email={lead?.email} name={lead?.name} />

      {/* Top match teaser */}
      {!matchesPending && matches.length > 0 && (
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'} shadow-sm`} data-testid="overview-top-match">
          <div className={`px-5 py-3 flex items-center justify-between ${isDark ? 'bg-gray-800/40 border-b border-gray-800' : 'bg-amber-50 border-b border-amber-200'}`}>
            <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              ★ Your top match
            </p>
            <button
              type="button"
              onClick={() => setActiveView('matches')}
              className={`text-xs font-semibold inline-flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
            >
              View all matches <ArrowRight size={12} />
            </button>
          </div>
          <div className="p-5">
            {(() => {
              const m = matches[0];
              const rv = m?.rv || {};
              return (
                <div className="flex flex-col sm:flex-row gap-4">
                  {rv.image_url && (
                    <img
                      src={rv.image_url}
                      alt={`${rv.year} ${rv.brand} ${rv.model}`}
                      className="w-full sm:w-48 h-32 object-cover rounded-xl shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold uppercase tracking-wide ${isDark ? 'text-[#F2A900]' : 'text-[#004E86]'}`}>
                      {m?.label || 'Best Match'}
                    </p>
                    <h3 className={`text-xl font-bold mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {rv.year} {rv.brand} {rv.model}
                    </h3>
                    <p className="text-2xl font-extrabold text-emerald-500 mt-1">${rv.price?.toLocaleString()}</p>
                    {rv.why_recommended && (
                      <p className={`text-sm italic mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>"{rv.why_recommended}"</p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Quick actions row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setActiveView('application')}
          className={`text-left rounded-2xl p-5 transition-all hover:shadow-md flex items-start gap-3 ${isDark ? 'bg-gray-900 border border-gray-800 hover:border-gray-700' : 'bg-white border border-amber-200 hover:border-amber-400'}`}
          data-testid="overview-action-retake"
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-50 text-rose-600'}`}>
            <History size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Retake or edit my application</p>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Tweak your answers and replace your matches with fresh ones.</p>
          </div>
          <ChevronRight size={16} className={isDark ? 'text-gray-600 mt-2' : 'text-gray-400 mt-2'} />
        </button>

        <button
          type="button"
          onClick={() => setActiveView('dealer')}
          className={`text-left rounded-2xl p-5 transition-all hover:shadow-md flex items-start gap-3 ${isDark ? 'bg-gray-900 border border-gray-800 hover:border-gray-700' : 'bg-white border border-amber-200 hover:border-amber-400'}`}
          data-testid="overview-action-call"
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
            <Phone size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Book a free 15-min call</p>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Get personalized guidance from a real RV expert.</p>
          </div>
          <ChevronRight size={16} className={isDark ? 'text-gray-600 mt-2' : 'text-gray-400 mt-2'} />
        </button>
      </div>
    </div>
  );
};


/**
 * DailyMatchesSection — VIP members see their refreshed-daily AI matches with
 * a recent-history list. Non-VIP see a blurred preview of one teaser match
 * + a marketing CTA to upgrade to VIP $197.
 */
const DailyMatchesSection = ({ isVipMember, lead, sessionId, isDark, setActiveView }) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (!isVipMember || !sessionId) { setLoadingHistory(false); return; }
    let active = true;
    axios.get(`${API}/vip/dashboard/${sessionId}`)
      .then(res => { if (active) setHistory(res.data?.match_history || []); })
      .catch(() => { if (active) setHistory([]); })
      .finally(() => { if (active) setLoadingHistory(false); });
    return () => { active = false; };
  }, [isVipMember, sessionId]);

  // VIP version — daily matches feed
  if (isVipMember) {
    return (
      <div className="space-y-6" data-testid="daily-matches-vip">
        {/* Status card */}
        <div className={`rounded-2xl p-5 sm:p-6 ${isDark ? 'bg-gradient-to-br from-amber-900/30 to-gray-900 border border-amber-700/40' : 'bg-gradient-to-br from-amber-50 to-white border border-amber-300'}`}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0 shadow-md">
              <Bell className="text-white" size={22} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Daily Matches Active</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-white">ON</span>
              </div>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Every morning we run a fresh AI scan of new inventory and email you the best 3 matches based on your application.
              </p>
              <p className={`text-xs mt-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                Last delivery: {history[0]?.sent_at ? new Date(history[0].sent_at).toLocaleString() : 'tomorrow morning'}
              </p>
            </div>
          </div>
        </div>

        {/* History list */}
        <div className={`rounded-2xl p-5 sm:p-6 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'}`}>
          <h4 className={`text-sm font-bold uppercase tracking-wide mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Recent daily deliveries
          </h4>
          {loadingHistory ? (
            <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={14} /><span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading…</span></div>
          ) : history.length === 0 ? (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Your first daily-matches email arrives tomorrow morning. Check back soon!
            </p>
          ) : (
            <div className="space-y-2">
              {history.slice(0, 14).map((h, i) => {
                const matchCount = (h.matches || []).length || h.match_count || 3;
                return (
                  <div key={i} className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${isDark ? 'border-gray-800 bg-gray-800/40' : 'border-amber-100 bg-amber-50/40'}`} data-testid={`daily-history-${i}`}>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {h.sent_at ? new Date(h.sent_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Recent delivery'}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{matchCount} new match{matchCount === 1 ? '' : 'es'}</p>
                    </div>
                    <Sparkles className="text-amber-500 shrink-0" size={16} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Non-VIP marketing version — blurred preview + VIP CTA
  return (
    <div className="space-y-6" data-testid="daily-matches-marketing">
      <div className={`relative overflow-hidden rounded-2xl p-6 sm:p-8 ${isDark ? 'bg-gradient-to-br from-amber-900/30 to-orange-900/20 border-2 border-amber-700/50' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300'}`}>
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#F2A900]/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#F2A900] text-black mb-3">
            <Crown size={10} /> VIP only
          </span>
          <h3 className={`text-2xl sm:text-3xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            NEVER MISS A GREAT DEAL
          </h3>
          <p className={`text-base mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            VIP members get fresh AI-curated RV matches delivered to their inbox <span className="font-bold">every single morning</span>, until they find their perfect rig. Inventory moves fast — and so do great deals.
          </p>

          {/* Blurred teaser preview */}
          <div className="relative my-5">
            <div
              aria-hidden="true"
              className={`select-none rounded-xl p-4 ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'}`}
              style={{ filter: 'blur(5px)', userSelect: 'none' }}
            >
              <div className="flex gap-4">
                <div className={`w-24 h-20 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <div className="flex-1 space-y-2">
                  <div className={`h-3 w-3/4 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-3 w-1/2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-3 w-2/3 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
                <div className={`h-6 w-16 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`px-4 py-2 rounded-full ${isDark ? 'bg-gray-900/80 text-amber-400 border border-amber-500/40' : 'bg-white/95 text-amber-700 border border-amber-300 shadow-md'} backdrop-blur-sm flex items-center gap-2 text-sm font-semibold`}>
                <Lock size={14} /> Sample daily match — VIP only
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-5">
            <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <CheckCircle size={16} className="text-emerald-500" /> Daily AI scan of new listings
            </div>
            <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <CheckCircle size={16} className="text-emerald-500" /> 2 ebooks ($78 value)
            </div>
            <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <CheckCircle size={16} className="text-emerald-500" /> All 3 buying tools ($597)
            </div>
          </div>

          <Button
            onClick={() => setActiveView('vip')}
            className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-base py-5 px-7 shadow-lg"
            data-testid="daily-marketing-vip-cta"
          >
            <Crown className="mr-2" size={18} />
            Upgrade to VIP — $197 (save $575)
          </Button>
        </div>
      </div>
    </div>
  );
};


/**
 * CriteriaSummary — pulled out of the previous inline "YOUR RV PREFERENCES" block.
 */
const CriteriaSummary = ({ lead, isDark }) => (
  <div className={`${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'} rounded-2xl shadow-sm p-6`} data-testid="criteria-summary">
    <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-[#004E86]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
      YOUR RV PREFERENCES
    </h3>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <PreferenceCard icon={<DollarSign size={20} />} label="Budget" value={`$${lead.matching_criteria.budget_max?.toLocaleString() || 'Flexible'}`} isDark={isDark} />
      <PreferenceCard icon={<Truck size={20} />} label="Type" value={lead.matching_criteria.rv_type?.replace(/_/g, ' ') || 'Any'} isDark={isDark} />
      <PreferenceCard icon={<Users size={20} />} label="Sleeps" value={lead.matching_criteria.sleep_capacity || 'Any'} isDark={isDark} />
      <PreferenceCard icon={<Zap size={20} />} label="Off-Grid" value={lead.matching_criteria.off_grid ? 'Yes' : 'No'} isDark={isDark} />
    </div>

    {lead.matching_criteria.tow_vehicle?.make && (
      <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-amber-200'}`}>
        <h4 className={`text-sm font-bold mb-3 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>YOUR TOW VEHICLE</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className={`${isDark ? 'bg-gray-800/50' : 'bg-amber-50'} rounded-lg p-3`}>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vehicle</p>
            <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {lead.matching_criteria.tow_vehicle.year} {lead.matching_criteria.tow_vehicle.make} {lead.matching_criteria.tow_vehicle.model}
            </p>
          </div>
          {lead.matching_criteria.tow_vehicle.engine && (
            <div className={`${isDark ? 'bg-gray-800/50' : 'bg-amber-50'} rounded-lg p-3`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Engine</p>
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{lead.matching_criteria.tow_vehicle.engine}</p>
            </div>
          )}
          {lead.matching_criteria.estimated_tow_capacity_lbs > 0 && (
            <div className={`${isDark ? 'bg-gray-800/50' : 'bg-amber-50'} rounded-lg p-3`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tow Capacity</p>
              <p className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                {lead.matching_criteria.estimated_tow_capacity_lbs?.toLocaleString()} lbs
              </p>
            </div>
          )}
          {lead.matching_criteria.tow_vehicle.tow_package && (
            <div className={`${isDark ? 'bg-gray-800/50' : 'bg-amber-50'} rounded-lg p-3`}>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tow Package</p>
              <p className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>✓ Equipped</p>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

/**
 * Preference Card Component
 */
const PreferenceCard = ({ icon, label, value, isDark }) => (
  <div className={`${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg p-4 text-center`}>
    <div className={`${isDark ? 'text-[#F2A900]' : 'text-[#004E86]'} mb-2 flex justify-center`}>{icon}</div>
    <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
    <p className={`font-bold capitalize ${isDark ? 'text-white' : 'text-gray-800'}`}>{value}</p>
  </div>
);

/**
 * RV Match Card Component
 */
const RVMatchCard = ({ match, index, isDark, sessionId, rvId, onTrackClick, onToggleFavorite, isFavorited, lead }) => {
  const rv = match.rv || {};
  const rankLabels = ['Best Match', 'Best Value', 'Budget Pick'];
  const rankColors = [
    'from-green-500 to-emerald-500',
    'from-blue-500 to-cyan-500',
    'from-amber-500 to-orange-500'
  ];

  // Fetch AI "Why this RV" explanation on mount — cached server-side on
  // first request so subsequent dashboard visits are instant + free.
  const [whyBlurb, setWhyBlurb] = useState(null);
  useEffect(() => {
    if (!sessionId || !rvId) return;
    let alive = true;
    const encodedRvId = encodeURIComponent(String(rvId));
    axios.get(`${API}/leads/${sessionId}/match-explanation/${encodedRvId}`)
      .then(res => { if (alive) setWhyBlurb(res.data?.explanation || null); })
      .catch(() => { /* silent — non-blocking UX */ });
    return () => { alive = false; };
  }, [sessionId, rvId]);
  
  // Check if this is a newly added match (within last 24 hours)
  const isNewMatch = match.source === 'dealer_generated' && match.added_at && 
    (new Date() - new Date(match.added_at)) < 24 * 60 * 60 * 1000;
  
  const handleSearchOnline = () => {
    // Track the click before opening the link (server + analytics, no PII)
    if (onTrackClick) {
      onTrackClick(rv, 'search');
    }
    // Localize with the customer's application ZIP (5-digit; silently omitted if invalid)
    const zipMatch = String(lead?.zipcode || '').trim().match(/^(\d{5})(-\d{4})?$/);
    const zip = zipMatch ? zipMatch[1] : null;
    const parts = [rv.year, rv.brand, rv.model].filter(Boolean).join(' ');
    const searchQuery = zip ? `${parts} for sale near ${zip}` : `${parts} for sale`;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'recommendation_search_clicked',
        recommendation_rank: index + 1,
        recommended_year: rv.year || null,
        recommended_brand: rv.brand || null,
        recommended_model: rv.model || null,
        match_score: match.match_score || null,
        location_search_used: !!zip,
      });
    } catch { /* noop */ }
    window.open(`https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank', 'noopener');
  };

  // Advisor assistance — attaches to the EXISTING lead, no new form, no duplicate lead
  const [helpState, setHelpState] = useState('idle');
  const handleAskAdvisor = async () => {
    if (helpState !== 'idle') return;
    setHelpState('sending');
    try {
      await axios.post(`${API}/track/click`, {
        session_id: sessionId,
        rv_year: rv.year,
        rv_brand: rv.brand,
        rv_model: rv.model,
        link_type: 'advisor_help',
        source: 'web',
      });
      setHelpState('done');
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'recommendation_help_requested',
          recommendation_rank: index + 1,
          recommended_year: rv.year || null,
          recommended_brand: rv.brand || null,
          recommended_model: rv.model || null,
          match_score: match.match_score || null,
        });
      } catch { /* noop */ }
    } catch {
      setHelpState('idle');
      toast.error('Could not send the request — please try again.');
    }
  };

  // Fire recommendations_viewed once per dashboard view (non-PII)
  useEffect(() => {
    if (index !== 0 || window.__rvmRecViewedFired) return;
    window.__rvmRecViewedFired = true;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'recommendations_viewed' });
    } catch { /* noop */ }
  }, [index]);

  const handleFavorite = (e) => {
    e.stopPropagation();
    const rvData = {
      id: `${rv.year}-${rv.brand}-${rv.model}-${index}`,
      name: `${rv.year} ${rv.brand} ${rv.model}`,
      year: rv.year,
      make: rv.brand,
      model: rv.model,
      price: rv.price,
      image: rv.image_url,
      type: rv.type,
    };
    if (onToggleFavorite) {
      onToggleFavorite(rvData);
    }
  };

  return (
    <div 
      className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden transition-all hover:shadow-xl ${isNewMatch ? 'ring-2 ring-green-500' : ''}`}
      data-testid={`rv-match-${index}`}
    >
      {/* NEW Badge for dealer-generated matches */}
      {isNewMatch && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center py-1.5 px-4">
          <span className="text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5">
            <Sparkles size={14} />
            NEW MATCH - Added by your advisor!
          </span>
        </div>
      )}
      
      <div className="md:flex">
        {/* Image Section - Only show if there's a real image URL */}
        {rv.image_url && (
          <div className="md:w-80 h-56 md:h-auto relative flex-shrink-0">
            <img 
              src={rv.image_url} 
              alt={`${rv.year} ${rv.brand} ${rv.model}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide the entire image container if image fails to load
                e.target.parentElement.style.display = 'none';
              }}
            />
            
            {/* Rank Badge */}
            <div className={`absolute top-4 left-4 bg-gradient-to-r ${rankColors[index] || rankColors[0]} text-white px-4 py-2 rounded-full shadow-lg`}>
              <div className="flex items-center gap-2">
                <Star size={16} fill="currentColor" />
                <span className="font-bold text-sm uppercase">{match.label || rankLabels[index]}</span>
              </div>
            </div>
          </div>
        )}

        {/* Details Section */}
        <div className="flex-1 p-6">
          {/* Rank Badge - Show inline if no image */}
          {!rv.image_url && (
            <div className={`inline-flex bg-gradient-to-r ${rankColors[index] || rankColors[0]} text-white px-4 py-2 rounded-full shadow-lg mb-4`}>
              <div className="flex items-center gap-2">
                <Star size={16} fill="currentColor" />
                <span className="font-bold text-sm uppercase">{match.label || rankLabels[index]}</span>
              </div>
            </div>
          )}
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className={`text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-[#F2A900]' : 'text-[#004E86]'}`}>
                #{index + 1} Match
              </p>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                {rv.year} {rv.brand} {rv.model}
              </h3>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-green-500">${rv.price?.toLocaleString()}</p>
              {rv.msrp_range && (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rv.msrp_range}</p>
              )}
            </div>
          </div>

          {/* Specs Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SpecBadge icon={<Truck size={14} />} label={rv.type?.replace(/_/g, ' ')} isDark={isDark} />
            <SpecBadge icon={<Users size={14} />} label={`Sleeps ${rv.sleeps}`} isDark={isDark} />
            <SpecBadge icon={<Ruler size={14} />} label={`${rv.length}' Length`} isDark={isDark} />
            {rv.off_grid && <SpecBadge icon={<Zap size={14} />} label="Off-Grid Ready" highlight isDark={isDark} />}
          </div>

          {/* Why This RV — AI-generated blurb explaining the match reasoning */}
          {whyBlurb && (
            <div
              className={`rounded-xl px-4 py-3 mb-4 flex items-start gap-3 border ${
                isDark
                  ? 'bg-cyan-500/10 border-cyan-400/25 text-cyan-100'
                  : 'bg-cyan-50 border-cyan-200 text-cyan-900'
              }`}
              data-testid={`why-this-rv-${index}`}
            >
              <Sparkles className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`} />
              <div className="min-w-0">
                <p className={`text-[10px] font-bold tracking-widest uppercase mb-0.5 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                  Why this RV
                </p>
                <p className="text-sm leading-relaxed">{whyBlurb}</p>
              </div>
            </div>
          )}

          {/* Weight Info for Towables */}
          {(rv.gvwr > 0 || rv.dry_weight > 0) && (
            <div className={`flex flex-wrap gap-2 mb-4 p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-amber-50'}`}>
              <span className={`text-xs font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                Tow Specs:
              </span>
              {rv.dry_weight > 0 && (
                <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Dry: {rv.dry_weight?.toLocaleString()} lbs
                </span>
              )}
              {rv.gvwr > 0 && (
                <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  GVWR: {rv.gvwr?.toLocaleString()} lbs
                </span>
              )}
              {rv.hitch_weight > 0 && (
                <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Hitch: {rv.hitch_weight?.toLocaleString()} lbs
                </span>
              )}
              {match.tow_compatible === true && (lead?.tow_vehicle?.tow_capacity || lead?.tow_vehicle?.capacity) && (
                <span className="text-xs text-green-600 font-semibold" data-testid={`tow-compat-${index}`}>
                  {(lead?.tow_vehicle?.tow_capacity_source || lead?.tow_vehicle?.capacitySource) === 'verified'
                    ? '✓ Within your TowIQ range'
                    : '✓ Within estimated TowIQ range'}
                </span>
              )}
            </div>
          )}

          {/* Features */}
          {rv.features && rv.features.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {rv.features.slice(0, 5).map((feature, fidx) => (
                <span 
                  key={fidx} 
                  className={`text-xs px-3 py-1 rounded-full ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}
                >
                  {feature}
                </span>
              ))}
            </div>
          )}

          {/* Why Recommended */}
          {rv.why_recommended && (
            <p className={`text-sm mb-4 italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              "{rv.why_recommended}"
            </p>
          )}

          {/* Action Button */}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <Button
                onClick={handleSearchOnline}
                className="bg-[#004E86] hover:bg-[#003d6b] text-white w-full"
                data-testid={`search-rv-${index}`}
              >
                <ExternalLink className="mr-2" size={16} />
                Search Online
              </Button>
              <p className={`text-[11px] mt-1 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Find this RV for sale near you
              </p>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleFavorite}
              className={`shrink-0 self-start ${isFavorited ? 'bg-red-50 border-red-300 text-red-500 hover:bg-red-100' : ''}`}
              data-testid={`favorite-rv-${index}`}
            >
              <Heart 
                size={18} 
                className={isFavorited ? 'fill-current' : ''} 
              />
            </Button>
          </div>
          {helpState === 'done' ? (
            <p className={`text-xs mt-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-testid={`advisor-help-confirm-${index}`}>
              ✓ Request received — we'll use your RV Match profile to help find this RV or a similar option.
            </p>
          ) : (
            <button
              onClick={handleAskAdvisor}
              disabled={helpState === 'sending'}
              className={`text-xs mt-2 underline underline-offset-2 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-60`}
              data-testid={`ask-advisor-${index}`}
            >
              {helpState === 'sending' ? 'Sending…' : 'Need help finding one? Ask an RV advisor →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Spec Badge Component
 */
const SpecBadge = ({ icon, label, highlight = false, isDark }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
    highlight 
      ? 'bg-green-100 text-green-700' 
      : isDark 
        ? 'bg-gray-700 text-gray-300' 
        : 'bg-gray-100 text-gray-700'
  }`}>
    {icon}
    <span className="capitalize">{label}</span>
  </div>
);

/**
 * Book a Free Call Section
 */
const BookCallSection = ({ sessionId, lead, isDark }) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [formData, setFormData] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    preferred_date: '',
    preferred_time: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/book-call`, {
        session_id: sessionId,
        ...formData
      });
      setIsBooked(true);
      toast.success('Call request submitted! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Failed to book call. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isBooked) {
    return (
      <div className={`${isDark ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'} border rounded-xl p-6 text-center`} data-testid="call-booked">
        <CheckCircle className="mx-auto mb-3 text-green-500" size={48} />
        <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-green-800'}`}>
          Call Request Submitted!
        </h3>
        <p className={isDark ? 'text-green-300' : 'text-green-700'}>
          We'll reach out to confirm your 15-minute consultation.
        </p>
      </div>
    );
  }

  return (
    <div className={`${isDark ? 'bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} border rounded-xl p-6`} data-testid="book-call-section">
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-blue-600' : 'bg-blue-500'}`}>
          <Phone className="text-white" size={28} />
        </div>
        <div className="flex-1">
          <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-[#004E86]'}`} style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            BOOK A FREE 15-MINUTE CALL
          </h3>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Get personalized guidance from an RV expert. We'll help clarify your needs and answer any questions.
          </p>

          {!showForm ? (
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="show-call-form-btn"
            >
              <Calendar className="mr-2" size={18} />
              Schedule My Call
            </Button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-gray-300' : ''}>Your Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}
                  />
                </div>
                <div>
                  <Label className={isDark ? 'text-gray-300' : ''}>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: formatUsPhoneInput(e.target.value)})}
                    required
                    className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}
                  />
                </div>
              </div>
              <div>
                <Label className={isDark ? 'text-gray-300' : ''}>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-gray-300' : ''}>Preferred Date *</Label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}
                  />
                </div>
                <div>
                  <Label className={isDark ? 'text-gray-300' : ''}>Preferred Time *</Label>
                  <select
                    value={formData.preferred_time}
                    onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                    required
                    className={`w-full px-3 py-2 rounded-md border text-sm ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option value="">Select a time</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className={isDark ? 'text-gray-300' : ''}>Notes (optional)</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any specific questions or topics you'd like to discuss?"
                  rows={2}
                  className={`w-full px-3 py-2 rounded-md border text-sm ${isDark ? 'bg-gray-800 border-gray-600 text-white placeholder:text-gray-500' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {isSubmitting ? <Loader2 className="animate-spin mr-2" size={18} /> : <CheckCircle className="mr-2" size={18} />}
                  Confirm Booking
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * VIP Membership Section
 */
const VIPMembershipSection = ({ sessionId, lead, isDark }) => {
  const [isVIP, setIsVIP] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkVIPStatus();
  }, [sessionId]);

  const checkVIPStatus = async () => {
    try {
      const res = await axios.get(`${API}/vip/status/${sessionId}`);
      setIsVIP(res.data.is_vip);
    } catch (err) {
      console.error('Error checking VIP status:', err);
    }
  };

  const handlePurchase = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/vip/create-payment`, {
        session_id: sessionId,
        email: lead?.email,
        name: lead?.name
      });
      
      if (res.data.approval_url) {
        window.location.href = res.data.approval_url;
      }
    } catch (error) {
      toast.error('Failed to start payment. Please try again.');
      setIsLoading(false);
    }
  };

  if (isVIP) {
    return (
      <div className={`${isDark ? 'bg-gradient-to-br from-amber-900/50 to-yellow-800/30 border-amber-600' : 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-300'} border-2 rounded-xl p-6`} data-testid="vip-active">
        <div className="flex items-center gap-3 mb-4">
          <Crown className="text-amber-500" size={32} />
          <div>
            <h3 className={`text-xl font-bold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>You're a VIP Member!</h3>
            <p className={`text-sm ${isDark ? 'text-amber-200/70' : 'text-amber-600'}`}>Daily matches and ebooks included</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to={`/vip/${sessionId}/dashboard`}>
            <Button variant="outline" className="w-full justify-start border-amber-400 text-amber-700 hover:bg-amber-100">
              <Crown className="mr-2" size={18} />
              VIP Dashboard
            </Button>
          </Link>
          <Link to={`/ebook/rv-buying-guide?token=${sessionId}`}>
            <Button variant="outline" className="w-full justify-start border-amber-400 text-amber-700 hover:bg-amber-100">
              <Book className="mr-2" size={18} />
              RV Buying Guide
            </Button>
          </Link>
          <Link to={`/ebook/rv-travel-guide?token=${sessionId}`}>
            <Button variant="outline" className="w-full justify-start border-amber-400 text-amber-700 hover:bg-amber-100">
              <Book className="mr-2" size={18} />
              RV Travel Guide
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${isDark ? 'bg-gradient-to-br from-amber-900/40 to-orange-900/30' : 'bg-gradient-to-br from-amber-50 to-orange-50'} rounded-xl border-2 ${isDark ? 'border-amber-600' : 'border-amber-400'}`} data-testid="vip-offer">
      {/* VIP Badge */}
      <div className="absolute top-0 right-0">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
          LIMITED OFFER
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0 shadow-lg">
            <Crown className="text-white" size={32} />
          </div>
          <div className="flex-1">
            <h3 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-[#004E86]'}`} style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              BECOME A VIP MEMBER
            </h3>
            <p className={`text-lg font-bold text-amber-600 mb-3`}>
              $197 <span className="text-sm font-normal text-gray-500">one-time payment · everything included</span>
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Daily Exact Matches</p>
                <span className="text-xs font-bold text-amber-600 whitespace-nowrap">$97 value</span>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>AI-powered RV matches sent to your inbox every day until you find your perfect RV</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>RV Buying Guide Ebook</p>
                <span className="text-xs font-bold text-amber-600 whitespace-nowrap">$39 value</span>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Complete guide covering RV types, budgeting, inspections, and negotiation strategies</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>RV Travel Guide Ebook</p>
                <span className="text-xs font-bold text-amber-600 whitespace-nowrap">$39 value</span>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Destinations, trip planning, campground tips, and life on the road secrets</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>All 3 RV Buying Tools</p>
                <span className="text-xs font-bold text-amber-600 whitespace-nowrap">$597 value</span>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Cost Calculator, Deal Analyzer & Negotiation Coach — lifetime access included.</p>
            </div>
          </div>
        </div>

        {/* Total value summary */}
        <div className={`mt-4 rounded-xl p-3 text-center ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-100 border border-amber-300'}`}>
          <p className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
            <span className="font-bold">Total value: $772</span> · You pay <span className="font-extrabold">$197</span> · <span className="font-bold text-emerald-600">Save $575</span>
          </p>
        </div>

        <div className="mt-6">
          <Button
            onClick={handlePurchase}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg py-6 shadow-lg"
            data-testid="vip-purchase-btn"
          >
            {isLoading ? (
              <Loader2 className="animate-spin mr-2" size={24} />
            ) : (
              <Gift className="mr-2" size={24} />
            )}
            Get VIP Access Now - $197
          </Button>
          <p className={`text-xs text-center mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Secure payment via PayPal. Instant access after purchase.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * SmsOptInCard — Lets customers opt in to future SMS match alerts. Twilio
 * isn't wired yet, so we just capture consent + phone and show a "Launching
 * soon" badge. Backend endpoint stores the preference so we can bulk-send
 * on day one when Twilio credentials are added.
 */
const SmsOptInCard = ({ sessionId, lead, isDark }) => {
  const [phone, setPhone] = useState('');
  const [optedIn, setOptedIn] = useState(false);
  const [providerActive, setProviderActive] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    axios.get(`${API}/leads/${sessionId}/sms-preferences`)
      .then(res => {
        if (!alive) return;
        setOptedIn(!!res.data.opted_in);
        setProviderActive(!!res.data.sms_provider_active);
        setPhone(res.data.phone || lead?.phone || '');
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [sessionId, lead?.phone]);

  const handleOptIn = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leads/${sessionId}/sms-preferences`, { phone, opted_in: true });
      setOptedIn(true);
      toast.success(providerActive ? 'You\u2019re signed up for SMS match alerts.' : 'On the list — we\u2019ll text you the moment SMS launches.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save preference');
    } finally { setLoading(false); }
  };

  const handleOptOut = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leads/${sessionId}/sms-preferences`, { phone, opted_in: false });
      setOptedIn(false);
      toast.success('SMS alerts turned off.');
    } catch (err) {
      toast.error('Could not update preference');
    } finally { setLoading(false); }
  };

  return (
    <div
      data-testid="sms-optin-card"
      className={`rounded-2xl p-4 sm:p-5 border ${
        isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
          isDark ? 'bg-cyan-500/15 border border-cyan-400/30 text-cyan-300' : 'bg-cyan-50 text-cyan-700'
        }`}>📲</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Get texted when a new match drops
            </h3>
            {!providerActive && (
              <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${
                isDark ? 'bg-amber-500/15 text-amber-300 border border-amber-400/30' : 'bg-amber-100 text-amber-800'
              }`}>Launching soon</span>
            )}
          </div>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {optedIn
              ? 'You\u2019re on the list. We\u2019ll text you the moment a new RV matches your criteria.'
              : 'Skip the daily email — get a quick text the instant a new RV matches your criteria. Reply STOP anytime to opt out.'}
          </p>
        </div>
      </div>

      {!optedIn ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            className={`flex-1 rounded-lg px-3 py-2 text-sm border transition-colors ${
              isDark
                ? 'bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 focus:ring-cyan-400 focus:border-cyan-400/60'
                : 'bg-white border-gray-300 text-gray-900 focus:ring-cyan-500'
            } focus:outline-none focus:ring-2`}
            data-testid="sms-optin-phone"
          />
          <button
            onClick={handleOptIn}
            disabled={loading}
            className="shrink-0 px-4 py-2 rounded-full bg-[#F2A900] hover:bg-amber-400 text-black text-sm font-bold disabled:opacity-50 transition-colors"
            data-testid="sms-optin-submit"
          >
            {loading ? 'Saving\u2026' : 'Text me matches'}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <span className={`text-sm font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
            ✓ Opted in {phone && `(${phone.slice(-4).padStart(phone.length, '•')})`}
          </span>
          <button
            onClick={handleOptOut}
            disabled={loading}
            className={`text-xs underline transition-opacity ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="sms-optout-btn"
          >
            Turn off
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * FreeResourcesSection — both flagship ebooks (RV Buying Guide, RV Travel
 * Guide) are now FREE for any customer who completed the application form.
 * Token in the URL is the session_id; the backend (/api/ebook/...) accepts
 * any session_id that belongs to a real lead.
 */
const FreeResourcesSection = ({ sessionId, isDark }) => {
  const guides = [
    {
      id: 'buying-guide',
      path: 'rv-buying-guide',
      eyebrow: 'Buying Playbook',
      title: "The Smart Buyer's RV Buying Guide",
      tagline: 'Buy the right RV at the right price. Avoid the traps.',
      bullets: [
        'Match the RV class to your real lifestyle',
        'The true 5–10 year cost of ownership',
        'Financing without getting cooked',
        'Advisor tactics + counter-scripts that work',
      ],
      meta: '9 chapters · ~11 pages',
      spine: 'from-blue-700 to-indigo-900',
      ring: 'border-blue-400/30',
      accent: 'text-blue-300',
      badgeBg: 'bg-blue-500/15',
      gradient: 'from-blue-600/40 via-blue-500/10 to-transparent',
    },
    {
      id: 'travel-guide',
      path: 'rv-travel-guide',
      eyebrow: 'Travel Playbook',
      title: 'The RV Travel & Camping Guide',
      tagline: 'Plan trips that work. Travel longer for less.',
      bullets: [
        'Routes, regions, and seasons that pay off',
        'Campground tiers — free to luxury',
        'Boondocking + off-grid power that lasts',
        'The travel-day routine that prevents disasters',
      ],
      meta: '9 chapters · ~11 pages',
      spine: 'from-emerald-700 to-teal-900',
      ring: 'border-emerald-400/30',
      accent: 'text-emerald-300',
      badgeBg: 'bg-emerald-500/15',
      gradient: 'from-emerald-600/40 via-emerald-500/10 to-transparent',
    },
  ];

  return (
    <div className="space-y-6" data-testid="free-resources-section">
      <div className={`rounded-2xl p-4 sm:p-5 ${isDark ? 'bg-emerald-500/10 border border-emerald-400/30 text-emerald-100' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
        <p className="text-sm font-bold">
          🎉 Both playbooks are unlocked — free with your application.
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-emerald-200/80' : 'text-emerald-700'}`}>
          Open them in your browser, print, or save as PDF. They're yours to keep.
        </p>
      </div>

      {/* Rig Ready TowIQ — free door-jamb scanner / VIN tow-rating checker.
          Lives here so shoppers hunting for the right rig can open it without
          leaving their match dashboard. */}
      <a
        href="/rigready"
        data-testid="resource-card-tow-iq"
        className={`group flex items-center gap-4 rounded-2xl border ${isDark ? 'border-cyan-400/30 bg-cyan-500/10 hover:bg-cyan-500/15' : 'border-cyan-300 bg-cyan-50 hover:bg-cyan-100'} p-4 sm:p-5 transition-colors`}
      >
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-cyan-400/20 text-cyan-300' : 'bg-cyan-500/15 text-cyan-700'}`}>
          <Truck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>Free Tool · Rig Ready</p>
          <h3 className={`text-base font-black leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>Rig Ready TowIQ — check your tow rating</h3>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            Snap your door-jamb sticker or paste your VIN — we&apos;ll pull GVWR, payload, and safe tow capacity in seconds.
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-black ${isDark ? 'bg-cyan-400 text-black group-hover:bg-cyan-300' : 'bg-cyan-600 text-white group-hover:bg-cyan-700'}`}>
          Open <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </a>

      {/* Trade Appraisal — buyers with a trade-in fill the certificate from
          their dashboard; contact info prefills via their session. */}
      <a
        href={`/trade-appraisal?session=${encodeURIComponent(sessionId || '')}`}
        data-testid="resource-card-trade-appraisal"
        className={`group flex items-center gap-4 rounded-2xl border ${isDark ? 'border-amber-400/30 bg-amber-500/10 hover:bg-amber-500/15' : 'border-amber-300 bg-amber-50 hover:bg-amber-100'} p-4 sm:p-5 transition-colors`}
      >
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-amber-400/20 text-amber-300' : 'bg-amber-500/15 text-amber-700'}`}>
          <ClipboardCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>Have a Trade-In?</p>
          <h3 className={`text-base font-black leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>Trade Appraisal — get your RV valued</h3>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            Complete the certificate online (add photos too) and your advisor starts your trade value before you visit.
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-black ${isDark ? 'bg-amber-400 text-black group-hover:bg-amber-300' : 'bg-amber-600 text-white group-hover:bg-amber-700'}`}>
          Start <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </a>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
        {guides.map((g) => (
          <a
            key={g.id}
            href={`/ebook/${g.path}?token=${encodeURIComponent(sessionId || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`resource-card-${g.id}`}
            className={`group relative rounded-3xl border ${g.ring} bg-gradient-to-br ${g.gradient} ${isDark ? 'backdrop-blur-sm' : ''} p-5 sm:p-6 flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-2xl`}
          >
            <div className={`absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${g.badgeBg} ${g.accent} text-[10px] font-bold tracking-widest uppercase`}>
              <CheckCircle className="w-3 h-3" /> Unlocked
            </div>

            <div className="flex items-stretch gap-4 mb-4">
              <div className={`relative shrink-0 w-20 h-28 sm:w-24 sm:h-32 rounded-md bg-gradient-to-br ${g.spine} shadow-[0_12px_30px_-10px_rgba(0,0,0,0.7)] ring-1 ring-white/10 overflow-hidden`}>
                <div className="absolute inset-y-0 left-0 w-1.5 bg-black/40" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.18),transparent_55%)]" />
                <div className="absolute inset-0 p-2.5 flex flex-col justify-between">
                  <BookOpen className="w-4 h-4 text-white/80" />
                  <div>
                    <p className="text-[7px] font-bold uppercase tracking-widest text-white/60">RV Match A.I.</p>
                    <p className="text-[9px] font-black leading-tight text-white mt-0.5 whitespace-pre">
                      {g.id === 'buying-guide' ? 'BUYING\nGUIDE' : 'TRAVEL\nGUIDE'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${g.accent}`}>{g.eyebrow}</p>
                <h3 className={`text-lg font-extrabold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{g.title}</h3>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{g.tagline}</p>
              </div>
            </div>

            <ul className="space-y-1.5 mb-4">
              {g.bullets.map((b) => (
                <li key={b} className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  <CheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${g.accent}`} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className={`mt-auto flex items-center justify-between gap-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{g.meta}</p>
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F2A900] text-black text-sm font-bold group-hover:bg-amber-400 transition-colors">
                Open <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default MyMatchesPage;
